// The desk's stage for home: a pinboard. Every project is a taped Polaroid
// (its photo, else its blueprint drawing) with the request as the caption,
// shelved by standing, the next step and the count written under it. A
// click opens the plan beside the list.
import { stepsDone, type Project } from '@shared/plan';
import type { RunProgress } from '@shared/artifacts';
import { groupedByStanding, type Standing } from '@shared/standing';
import { Chip, Polaroid, SectionHeader, cx } from '@app/ui';
import { useProjects, useRuns, useStoreReady } from '@app/store/hooks';
import { buildingIds, failedIds } from '@app/store/store';
import { useBlueprintSrc, usePhotoSrc } from '@app/store/media';
import { useNav } from '../../router';
import { useStandIn } from '../../onboarding/standIn';
import { subtitleOf } from './ProjectRow';

export function Pinboard() {
  const nav = useNav();
  const ready = useStoreReady();
  const projects = useProjects();
  const runs = useRuns();
  // The tour's demo project, pinned with the rest while the tour shows it.
  const standIn = useStandIn();
  const groups = groupedByStanding(standIn?.project ? [standIn.project, ...projects] : [...projects], buildingIds(runs), failedIds(runs));
  const count = groups.reduce((n, g) => n + g.projects.length, 0);
  return (
    <div className="board">
      <div className="stagebar">
        <h2>Your projects</h2>
        {ready && <Chip>{count} on the board</Chip>}
      </div>
      {groups.map((g) => (
        <section key={g.standing}>
          <SectionHeader count={g.projects.length}>{g.title}</SectionHeader>
          <div className="shelf">
            {g.projects.map((p, i) => <Pin key={p.id} project={p} standing={g.standing} run={runs.get(p.id)} tilt={((i * 7) % 5 - 2) * 1.2} onOpen={() => nav.push(`/plan/${p.id}`)} />)}
          </div>
        </section>
      ))}
      {ready && count === 0 && <p className="note board-note">Nothing on the board yet. Press + to snap a spot and get your first plan.</p>}
      {count > 0 && <p className="note board-note">Click a picture to open its plan beside the list.</p>}
    </div>
  );
}

function Pin({ project: p, standing, run, tilt, onOpen }: { project: Project; standing: Standing; run?: RunProgress; tilt: number; onOpen: () => void }) {
  const photo = usePhotoSrc(p);
  const blueprint = useBlueprintSrc(p);
  const done = stepsDone(p), total = standing === 'building' ? 0 : p.steps.length;
  const under = subtitleOf(p, standing, run);
  return (
    <a className={cx('pin', !photo && 'bp')} href={`/plan/${p.id}`} onClick={(e) => { e.preventDefault(); onOpen(); }}>
      <Polaroid src={photo ?? blueprint} alt={p.title} caption={p.request ?? p.title} seed={p.id} tilt={tilt} width={190} />
      <span className="pin-under"><b className="clamp1">{under}</b>{total > 0 && <span className="num">{done}/{total}</span>}</span>
    </a>
  );
}
